import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["editor", "counter", "readingTime", "grade", "subject"]

  connect() {
    this.boundUpdateWordCount = this.updateWordCount.bind(this)
    this.updateWordCount()

    if (this.hasEditorTarget) {
      this.editorTarget.addEventListener("trix-change", this.boundUpdateWordCount)
    }
    if (this.hasSubjectTarget) {
      this.subjectTarget.addEventListener("keyup", this.boundUpdateWordCount)
      this.subjectTarget.addEventListener("input", this.boundUpdateWordCount)
    }
  }

  disconnect() {
    if (this.hasEditorTarget) {
      this.editorTarget.removeEventListener("trix-change", this.boundUpdateWordCount)
    }
    if (this.hasSubjectTarget) {
      this.subjectTarget.removeEventListener("keyup", this.boundUpdateWordCount)
      this.subjectTarget.removeEventListener("input", this.boundUpdateWordCount)
    }
  }

  updateWordCount() {
    if (!this.hasEditorTarget) {
      return
    }

    const bodyText = this.editorTarget.editor.getDocument().toString().trim()
    const subjectText = this.hasSubjectTarget ? (this.subjectTarget.value || "").trim() : ""
    const fullText = [subjectText, bodyText].filter((t) => t.length > 0).join(" ")

    const wordCount = bodyText ? bodyText.split(/\s+/).filter((word) => word.length > 0).length : 0

    if (this.hasCounterTarget) {
      this.counterTarget.textContent = wordCount
    }

    if (this.hasReadingTimeTarget) {
      this.updateReadingTime(fullText)
    }

    if (this.hasGradeTarget) {
      this.updateGrade(fullText)
    }
  }

  updateReadingTime(text) {
    if (!text || text.trim().length === 0) {
      this.readingTimeTarget.textContent = "0 sec."
      return
    }

    const wordCount = text.split(/\s+/).filter((word) => word.length > 0).length
    if (wordCount === 0) {
      this.readingTimeTarget.textContent = "0 sec."
      return
    }

    // Average reading speed is 200-250 words per minute, using 225 as average
    const seconds = Math.ceil((wordCount / 225.0) * 60)

    if (seconds < 60) {
      this.readingTimeTarget.textContent = `${seconds} sec.`
    } else {
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      if (remainingSeconds === 0) {
        this.readingTimeTarget.textContent = `${minutes} min.`
      } else {
        this.readingTimeTarget.textContent = `${minutes} min. ${remainingSeconds} sec.`
      }
    }
  }

  updateGrade(text) {
    if (!text || text.trim().length === 0) {
      this.gradeTarget.textContent = "-"
      this.updateTooltip("")
      return
    }

    const grade = this.calculateFleschKincaidGrade(text)
    const tooltipText = this.getGradeExplanation(grade)

    if (grade === null) {
      this.gradeTarget.textContent = "-"
    } else {
      const gradeInt = Math.floor(grade)
      this.gradeTarget.textContent = gradeInt.toString()
    }

    this.updateTooltip(tooltipText)
  }

  updateTooltip(text) {
    if (!this.gradeTarget) return

    if (text) {
      this.gradeTarget.setAttribute("data-bs-original-title", text)
    } else {
      this.gradeTarget.removeAttribute("data-bs-original-title")
    }

    if (window.bootstrap?.Tooltip) {
      const tooltipInstance = window.bootstrap.Tooltip.getInstance(this.gradeTarget)
      if (tooltipInstance) {
        if (text) {
          tooltipInstance.setContent({ ".tooltip-inner": text })
        } else {
          tooltipInstance.dispose()
        }
      }
    }
  }

  getGradeExplanation(grade) {
    const gradeInt = Math.floor(grade)

    if (grade === null) {
      return i18n.t("js.components.campaign.content.grade_explanation.unknown")
    } else if (gradeInt <= 5) {
      return i18n.t("js.components.campaign.content.grade_explanation.very_easy", { grade: gradeInt })
    } else if (gradeInt <= 8) {
      return i18n.t("js.components.campaign.content.grade_explanation.easy", { grade: gradeInt })
    } else if (gradeInt === 9) {
      return i18n.t("js.components.campaign.content.grade_explanation.average", { grade: gradeInt })
    } else if (gradeInt <= 12) {
      return i18n.t("js.components.campaign.content.grade_explanation.fairly_difficult", { grade: gradeInt })
    } else if (gradeInt <= 16) {
      return i18n.t("js.components.campaign.content.grade_explanation.difficult", { grade: gradeInt })
    } else {
      return i18n.t("js.components.campaign.content.grade_explanation.very_difficult", { grade: gradeInt })
    }
  }

  // Calculate Flesch-Kincaid Grade Level
  // Formula: 0.39 * (total_words / total_sentences) + 11.8 * (total_syllables / total_words) - 15.59
  // Returns U.S. school grade level (0-20), where lower grades indicate easier reading
  calculateFleschKincaidGrade(text) {
    const sentences = text.split(/[.!?]+/).filter((s) => s.trim().length > 0)
    if (sentences.length === 0) {
      return null
    }

    const words = text.split(/\s+/).filter((w) => w.trim().length > 0)
    if (words.length === 0) {
      return null
    }

    const totalWords = words.length
    if (totalWords < 5) {
      return null
    }

    const totalSentences = sentences.length
    const totalSyllables = words.reduce((sum, word) => sum + this.countSyllables(word), 0)

    if (totalSyllables === 0) {
      return null
    }

    const grade =
      0.39 * (totalWords / totalSentences) + 11.8 * (totalSyllables / totalWords) - 15.59

    return Math.max(0, Math.min(20, grade))
  }

  // Count syllables using a more accurate algorithm for Flesch-Kincaid
  // Based on standard syllable counting rules
  countSyllables(word) {
    word = word.toLowerCase().replace(/[^a-z]/g, "")
    if (word.length === 0) {
      return 0
    }

    const wordClean = word.replace(/[aeiouy]+/g, "a").replace(/[^a]/g, "")
    let syllableCount = wordClean.length

    if (word.endsWith("e") && syllableCount > 1) {
      syllableCount -= 1
    }

    if (word.length > 2 && word.endsWith("le") && !/[aeiouy]/.test(word[word.length - 3])) {
      syllableCount += 1
    }

    return Math.max(syllableCount, 1)
  }
}

